/* ==========================================================================
   BOD – product page components
   - <bod-delivery>: estimated delivery dates (business days, Casablanca time).
   - <bod-upsell>: one-tap add to cart for the "same collection" upsell rows.
   - <bod-sticky-atc>: sticky add to cart that drives the theme's own product form.
   ========================================================================== */
(() => {
  if (window.BodProductLoaded) return;
  window.BodProductLoaded = true;

  const capitalize = text => text.charAt(0).toUpperCase() + text.slice(1);

  // Shopify money format (e.g. "{{amount}} dh") applied to cents.
  function formatMoney(cents, format) {
    const pattern = format || '{{amount}}';
    const number = (value, precision, thousands = ',', decimal = '.') => {
      const parts = (value / 100).toFixed(precision).split('.');
      return parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`) + (parts[1] ? decimal + parts[1] : '');
    };
    return pattern.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return number(cents, 0);
        case 'amount_with_comma_separator':
          return number(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return number(cents, 0, '.', ',');
        case 'amount_no_decimals_with_space_separator':
          return number(cents, 0, ' ');
        case 'amount_with_space_separator':
          return number(cents, 2, ' ', ',');
        case 'amount_with_apostrophe_separator':
          return number(cents, 2, "'", '.');
        default:
          return number(cents, 2);
      }
    });
  }

  const escapeHtml = text => String(text).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const cartDrawer = () => document.querySelector('cart-drawer');

  /* Estimated delivery ------------------------------------------------------- */
  class BodDelivery extends HTMLElement {
    connectedCallback() {
      if (this.isMounted) return;
      this.isMounted = true;
      this.render();
      this.timer = setInterval(() => this.render(), 60 * 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.isMounted = false;
    }

    number(name, fallback) {
      const value = parseInt(this.dataset[name], 10);
      return Number.isNaN(value) ? fallback : value;
    }

    today() {
      try {
        const parts = {};
        new Intl.DateTimeFormat('en-GB', { timeZone: this.dataset.timezone || 'Africa/Casablanca', year: 'numeric', month: 'numeric', day: 'numeric' })
          .formatToParts(new Date())
          .forEach(part => (parts[part.type] = Number(part.value)));
        return new Date(parts.year, parts.month - 1, parts.day);
      } catch (error) {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), now.getDate());
      }
    }

    addBusinessDays(date, days) {
      const skip = (this.dataset.skipDays || '').split(',').filter(Boolean).map(Number);
      const result = new Date(date);
      let added = 0;
      while (added < days) {
        result.setDate(result.getDate() + 1);
        if (!skip.includes(result.getDay())) added += 1;
      }
      return result;
    }

    format(date, withMonth) {
      const locale = this.dataset.locale || 'en';
      const weekday = capitalize(new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date));
      const day = date.getDate();
      const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);
      return withMonth ? `${weekday} ${day} ${month}` : `${weekday} ${day}`;
    }

    range(from, to) {
      if (from.getTime() === to.getTime()) return this.format(from, true);
      if (from.getMonth() === to.getMonth()) return `${this.format(from, false)} – ${this.format(to, true)}`;
      return `${this.format(from, true)} – ${this.format(to, true)}`;
    }

    render() {
      const shipMin = this.number('shipMin', 1);
      const shipMax = Math.max(shipMin, this.number('shipMax', 2));
      const deliveryMin = Math.max(shipMin, this.number('deliveryMin', 8));
      const deliveryMax = Math.max(deliveryMin, this.number('deliveryMax', 10));
      const today = this.today();
      const shipped = this.querySelector('[data-bod-shipped]');
      const delivered = this.querySelector('[data-bod-delivered]');
      if (shipped) shipped.textContent = this.range(this.addBusinessDays(today, shipMin), this.addBusinessDays(today, shipMax));
      if (delivered) delivered.textContent = this.range(this.addBusinessDays(today, deliveryMin), this.addBusinessDays(today, deliveryMax));
    }
  }
  if (!customElements.get('bod-delivery')) customElements.define('bod-delivery', BodDelivery);

  /* Upsell ------------------------------------------------------------------- */
  class BodUpsell extends HTMLElement {
    connectedCallback() {
      if (this.isMounted) return;
      this.isMounted = true;
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
    }

    onChange = event => {
      const select = event.target.closest('[data-bod-up-select]');
      if (!select) return;
      const option = select.selectedOptions[0];
      const price = select.closest('[data-bod-up-row]').querySelector('[data-bod-up-price]');
      if (option && price && option.dataset.price) price.textContent = option.dataset.price;
    };

    onClick = event => {
      const button = event.target.closest('[data-bod-up-add]');
      if (!button || button.classList.contains('is-loading')) return;
      event.preventDefault();
      const row = button.closest('[data-bod-up-row]');
      const select = row.querySelector('[data-bod-up-select]');
      this.add(select ? select.value : row.dataset.variantId, button);
    };

    async add(variantId, button) {
      const drawer = cartDrawer();
      button.classList.add('is-loading');
      button.setAttribute('aria-disabled', 'true');
      const body = { items: [{ id: Number(variantId), quantity: 1 }] };
      if (drawer) {
        body.sections = drawer.getSectionsToRender().map(section => section.id);
        body.sections_url = window.location.pathname;
      }
      try {
        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        });
        const state = await response.json();
        if (!response.ok || state.status) throw new Error(state.description || state.message || 'Error');

        if (!drawer) {
          window.location = window.routes.cart_url;
          return;
        }
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bod-upsell', productVariantId: variantId, cartData: state });
        }
        drawer.classList.remove('is-empty');
        drawer.setActiveElement?.(button);
        drawer.renderContents(state);
        button.classList.add('is-added');
        setTimeout(() => button.classList.remove('is-added'), 1800);
      } catch (error) {
        const label = button.querySelector('.bod-up__add-label');
        if (label) {
          const text = label.textContent;
          label.textContent = error.message.length < 40 ? error.message : 'Error';
          setTimeout(() => (label.textContent = text), 2200);
        }
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      }
    }
  }
  if (!customElements.get('bod-upsell')) customElements.define('bod-upsell', BodUpsell);

  /* Sticky add to cart ------------------------------------------------------- */
  const CHEVRON =
    '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.5 4.5 6 8l3.5-3.5"/></svg>';

  class BodStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.isMounted) return;
      // Fixed bars must not live inside transformed / sticky parents.
      if (this.parentElement !== document.body) {
        // Theme editor: a re-rendered section brings a new bar, drop the old one.
        document.querySelectorAll(`body > bod-sticky-atc[data-section="${this.dataset.section}"]`).forEach(old => old.remove());
        document.body.appendChild(this);
        return; // connectedCallback runs again after the move
      }
      try {
        this.data = JSON.parse(this.querySelector('[data-st-data]').textContent);
      } catch (error) {
        return;
      }
      this.section = this.dataset.section;
      this.productForm = document.getElementById(`product-form-${this.section}`)?.closest('product-form');
      if (!this.productForm || !this.data.variants.length) return;
      this.isMounted = true;
      this.init();
    }

    init() {
      this.form = this.productForm.querySelector('form');
      this.idInput = this.form.querySelector('input[name="id"]');
      this.info = this.productForm.closest('product-info') || document.querySelector(`product-info[data-section="${this.section}"]`);
      this.chips = this.querySelector('[data-st-chips]');
      this.panel = this.querySelector('[data-st-panel]');
      this.panelTitle = this.querySelector('[data-st-panel-title]');
      this.panelBody = this.querySelector('[data-st-panel-body]');
      this.thumb = this.querySelector('.bod-st__thumb img');
      this.priceEl = this.querySelector('[data-st-price]');
      this.compareEl = this.querySelector('[data-st-compare]');
      this.cta = this.querySelector('[data-st-cta]');
      this.ctaLabel = this.querySelector('[data-st-cta-label]');
      this.openKey = null;
      this.visible = false;

      this.options = this.data.hasOnlyDefault ? [] : this.data.options.map((name, index) => ({ name, index, values: this.data.values[index] || [] }));

      this.addEventListener('click', this.onClick);
      document.addEventListener('click', this.onOutsideClick);
      document.addEventListener('keydown', this.onKeyDown);
      // The theme updates the variant id (and fires "change") whenever the picker changes.
      document.addEventListener('change', this.onPageChange);
      this.cta.addEventListener('click', this.onCta);

      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.endLoading(true));
        subscribe(PUB_SUB_EVENTS.cartError, () => this.endLoading(false));
      }

      this.observeMainButton();
      this.watchOverlays();
      this.render();
    }

    /* State ---------------------------------------------------------------- */
    get quantityInput() {
      return this.info?.querySelector('.quantity__input') || document.querySelector(`.quantity__input[form="product-form-${this.section}"]`);
    }

    get quantity() {
      const input = this.quantityInput;
      return Math.max(1, parseInt(input && input.value, 10) || 1);
    }

    currentVariant() {
      const id = Number(this.idInput && this.idInput.value);
      return this.data.variants.find(variant => variant.id === id) || this.data.variants[0];
    }

    findVariant(options) {
      return this.data.variants.find(variant => variant.options.every((value, i) => value === options[i]));
    }

    valueAvailable(index, value) {
      return this.data.variants.some(variant => variant.options[index] === value && variant.available);
    }

    /* Main form ------------------------------------------------------------ */
    pickers() {
      const selects = this.info?.querySelector('variant-selects');
      return selects ? Array.from(selects.querySelectorAll(':scope > .product-form__input')) : [];
    }

    // Selects the option values in the theme's own picker, so price, media, URL and the
    // variant id update the usual way. Falls back to the variant id when there is no picker.
    selectVariant(variant) {
      const pickers = this.pickers();
      let last = null;
      if (pickers.length === this.options.length) {
        variant.options.forEach((value, i) => {
          const picker = pickers[i];
          const select = picker.querySelector('select');
          if (select) {
            if (select.value !== value) {
              select.value = value;
              last = select;
            }
            return;
          }
          const radio = Array.from(picker.querySelectorAll('input[type="radio"]')).find(input => input.value === value);
          if (radio && !radio.checked) {
            radio.checked = true;
            last = radio;
          }
        });
      }
      if (last) {
        last.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (this.idInput && Number(this.idInput.value) !== variant.id) {
        this.idInput.value = variant.id;
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    choose(index, value) {
      const current = this.currentVariant();
      const next = current.options.slice();
      next[index] = value;
      let variant = this.findVariant(next);
      if (!variant || !variant.available) {
        // Closest available combination with this value.
        let best = null;
        let bestScore = -1;
        this.data.variants.forEach(candidate => {
          if (candidate.options[index] !== value || !candidate.available) return;
          const score = candidate.options.reduce((total, v, i) => total + (v === current.options[i] ? 1 : 0), 0);
          if (score > bestScore) {
            best = candidate;
            bestScore = score;
          }
        });
        variant = best || variant;
      }
      if (variant) this.selectVariant(variant);
    }

    setQuantity(quantity) {
      const input = this.quantityInput;
      if (!input) return;
      input.value = quantity;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* Rendering ------------------------------------------------------------ */
    render() {
      this.renderChips();
      this.renderPanel();
      this.renderSummary();
    }

    renderChips() {
      const variant = this.currentVariant();
      let html = this.options
        .map(
          option =>
            `<button type="button" class="bod-st__chip" data-key="o${option.index}" aria-expanded="${this.openKey === `o${option.index}`}" aria-label="${escapeHtml(option.name)}: ${escapeHtml(variant.options[option.index])}">${escapeHtml(variant.options[option.index])}${CHEVRON}</button>`
        )
        .join('');
      if (this.quantityInput) {
        html += `<button type="button" class="bod-st__chip" data-key="qty" aria-expanded="${this.openKey === 'qty'}">Qty: ${this.quantity}${CHEVRON}</button>`;
      }
      this.chips.innerHTML = html;
      this.chips.hidden = !html;
    }

    renderPanel() {
      if (!this.openKey) {
        this.panel.classList.remove('is-open');
        return;
      }
      let html = '<div class="bod-st__pills">';
      if (this.openKey === 'qty') {
        this.panelTitle.textContent = 'Quantity';
        const quantity = this.quantity;
        for (let q = 1; q <= 6; q++) {
          html += `<button type="button" class="bod-st__pill${q === quantity ? ' is-selected' : ''}" data-qty="${q}">${q}</button>`;
        }
      } else {
        const index = parseInt(this.openKey.slice(1), 10);
        const option = this.options.find(o => o.index === index);
        const variant = this.currentVariant();
        this.panelTitle.textContent = option.name;
        option.values.forEach(value => {
          const available = this.valueAvailable(index, value);
          html += `<button type="button" class="bod-st__pill${variant.options[index] === value ? ' is-selected' : ''}"${available ? '' : ' disabled'} data-pos="${index}" data-val="${escapeHtml(value)}">${escapeHtml(value)}</button>`;
        });
      }
      html += '</div>';
      this.panelBody.innerHTML = html;
      this.panel.classList.add('is-open');
    }

    renderSummary() {
      const variant = this.currentVariant();
      const quantity = this.quantity;
      const format = this.data.moneyFormat;
      if (this.thumb && variant.image && this.thumb.getAttribute('src') !== variant.image) {
        this.thumb.removeAttribute('srcset');
        this.thumb.src = variant.image;
      }
      const total = variant.price * quantity;
      this.priceEl.textContent = formatMoney(total, format);
      this.compareEl.textContent = variant.compare > variant.price ? formatMoney(variant.compare * quantity, format) : '';
      const mainDisabled = this.productForm.querySelector('[type="submit"]')?.hasAttribute('disabled');
      if (!variant.available || mainDisabled) {
        this.cta.disabled = true;
        this.ctaLabel.textContent = variant.available ? this.dataset.unavailableLabel : this.dataset.soldOutLabel;
      } else {
        this.cta.disabled = false;
        this.ctaLabel.textContent = this.dataset.addLabel;
      }
    }

    /* Events --------------------------------------------------------------- */
    onClick = event => {
      event.bodStickyInside = true; // re-rendering detaches event.target
      const chip = event.target.closest('.bod-st__chip');
      if (chip) {
        const key = chip.dataset.key;
        this.openKey = this.openKey === key ? null : key;
        this.render();
        return;
      }
      const pill = event.target.closest('.bod-st__pill');
      if (!pill || pill.disabled) return;
      if (pill.dataset.qty) this.setQuantity(Number(pill.dataset.qty));
      else this.choose(Number(pill.dataset.pos), pill.dataset.val);
      this.openKey = null;
      this.render();
    };

    onOutsideClick = event => {
      if (this.openKey && !event.bodStickyInside) {
        this.openKey = null;
        this.render();
      }
    };

    onKeyDown = event => {
      if (event.key === 'Escape' && this.openKey) {
        this.openKey = null;
        this.render();
      }
    };

    onPageChange = event => {
      if (this.contains(event.target)) return;
      if (event.target === this.idInput || event.target.classList?.contains('quantity__input') || event.target.closest?.('variant-selects')) {
        // Wait for the theme to finish updating the form.
        requestAnimationFrame(() => this.render());
      }
    };

    onCta = () => {
      const submit = this.productForm.querySelector('[type="submit"][name="add"]') || this.productForm.querySelector('[type="submit"]');
      if (!submit || this.cta.disabled) return;
      this.openKey = null;
      this.renderPanel();
      this.cta.classList.add('is-loading');
      this.added = false;
      submit.click();
      // An app (e.g. required product options) may stop the add: show the form instead.
      clearTimeout(this.checkTimer);
      this.checkTimer = setTimeout(() => {
        if (this.added || submit.classList.contains('loading')) return;
        this.endLoading(false);
        this.form.closest('.product__info-container')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 1200);
      clearTimeout(this.loadingTimer);
      this.loadingTimer = setTimeout(() => this.endLoading(false), 8000);
    };

    endLoading(added) {
      if (added) this.added = true;
      if (!this.cta.classList.contains('is-loading')) return;
      this.cta.classList.remove('is-loading');
      if (added) {
        this.cta.classList.add('is-done');
        setTimeout(() => this.cta.classList.remove('is-done'), 1600);
      }
    }

    /* Visibility ----------------------------------------------------------- */
    observeMainButton() {
      const target = this.productForm.querySelector('.product-form__buttons') || this.productForm;
      if (!('IntersectionObserver' in window)) return;
      this.mainVisible = true;
      new IntersectionObserver(([entry]) => {
        // Shown once the main button has scrolled above the screen, hidden when it is back.
        this.passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.updateVisibility();
      }).observe(target);
    }

    watchOverlays() {
      const drawer = cartDrawer();
      const update = () => this.updateVisibility();
      if (drawer) new MutationObserver(update).observe(drawer, { attributes: true, attributeFilter: ['class'] });
      // Releasit adds its order form to the page later.
      this.overlayTimer = setInterval(() => {
        const modal = document.getElementById('_rsi-cod-form-modal');
        if (!modal) return;
        new MutationObserver(update).observe(modal, { attributes: true, attributeFilter: ['class'] });
        clearInterval(this.overlayTimer);
      }, 1000);
    }

    updateVisibility() {
      const drawerOpen = cartDrawer()?.classList.contains('active');
      const modalOpen = document.getElementById('_rsi-cod-form-modal')?.classList.contains('_rsi-cod-form-modal-open');
      const show = !!this.passed && !drawerOpen && !modalOpen;
      if (show === this.visible) return;
      this.visible = show;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      this.toggleAttribute('inert', !show);
      document.body.classList.toggle('bod-st-on', show);
      if (show) document.body.style.setProperty('--bod-st-height', `${this.querySelector('.bod-st__card').offsetHeight + 16}px`);
      if (!show && this.openKey) {
        this.openKey = null;
        this.render();
      }
    }
  }
  if (!customElements.get('bod-sticky-atc')) customElements.define('bod-sticky-atc', BodStickyAtc);
})();
